/** Web Push client: service-worker registration and subscription lifecycle. */

import { apiFetch } from "@/services/api-client";

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function registerServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    await navigator.serviceWorker.register("/sw.js");
  } catch {
    // Non-fatal: the app works fully without the worker.
  }
}

function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

export async function getSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/** Ask permission, subscribe with the server's VAPID key, store server-side. */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false;

  const { public_key } = await apiFetch<{ public_key: string }>("/push/vapid-public-key");
  if (!public_key) return false;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(public_key) as BufferSource,
    }));

  const json = subscription.toJSON();
  await apiFetch("/push/subscriptions", {
    method: "POST",
    body: {
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
    },
  });
  return true;
}

export async function disablePush(): Promise<void> {
  const subscription = await getSubscription();
  if (!subscription) return;
  await apiFetch("/push/unsubscribe", {
    method: "POST",
    body: { endpoint: subscription.endpoint },
  });
  await subscription.unsubscribe();
}
